import flatpickr from 'flatpickr';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Unified date picker init — Phase 183 (INPUT-01, D-11, D-13, D-15)
 *
 * Selects all [data-picker="date"] inputs and initializes flatpickr with the
 * locked config (dateFormat Y-m-d + allowInput:true).
 *
 * Idempotency guard: flatpickr sets `_flatpickr` on the element after init.
 * Checking it prevents double-init when shared.init() runs on every page.
 *
 * disableMobile is intentionally NOT set (default = false) so mobile users get
 * the native OS date picker (D-11/INPUT-04).
 *
 * Supported opt-in attributes:
 * - `data-max-date="today"` (Phase 262, P-03) — caps the picker UI at today. This is only
 *   HALF of the P-03 cap: a maxDate alone still lets an arrow tap increment the modal's date
 *   state past today, so the home dashboard's date-nav controller (src/js/home/date-nav.ts)
 *   enforces the same cap a second time in its next-arrow handler.
 * - `data-max-date="YYYY-MM-DD"` / `data-min-date="YYYY-MM-DD"` (Phase 287, ARCH-04) — an
 *   ISO-shaped literal bounds the picker to a live server-resolved rule-coverage window
 *   (Archetypes is the first consumer). This is a UI guardrail, not a boundary: flatpickr's
 *   minDate/maxDate constrain the calendar and typed input, but a hand-crafted
 *   ?arc_from=/?arc_to= query string reaches the server untouched, so the enforcing control
 *   is always the presenter's own server-side branch (ArchetypesPresenter's $windowIsEmpty,
 *   287-10) — this attribute is defence in depth on top of it, never a substitute for it.
 *   A malformed or empty value (e.g. Latte's empty-string render of a null rule bound) sets
 *   nothing and is inert by design.
 */
export function initPickers(): void {
    const inputs = document.querySelectorAll<HTMLInputElement>('[data-picker="date"]');
    if (!inputs.length) return; // Guard C: do nothing on pages without pickers

    inputs.forEach(el => {
        if ((el as any)._flatpickr) return; // per-element idempotency guard (D-15)
        const config: flatpickr.Options.Options = {
            dateFormat: 'Y-m-d',  // LOCKED — matches PHP-side validation (D-12)
            allowInput: true,      // D-11: manual keyboard entry
            // disableMobile: false (default) — native OS picker on mobile (D-11/D-03)
        };
        if (el.dataset.maxDate === 'today') {
            config.maxDate = new Date();
        } else if (el.dataset.maxDate && ISO_DATE.test(el.dataset.maxDate)) {
            // Y-m-d string, not `new Date(...)` — constructing a Date from a bare
            // calendar-date string would reintroduce a UTC-vs-local off-by-one this
            // project has shipped before (Phase 287 / ARCH-04).
            config.maxDate = el.dataset.maxDate;
        }
        if (el.dataset.minDate && ISO_DATE.test(el.dataset.minDate)) {
            // No 'today' keyword on the min side — nothing consumes one, so it is
            // not invented here; an unused keyword would be a future footgun.
            config.minDate = el.dataset.minDate;
        }
        flatpickr(el, config);
    });
}
